import {Component, computed, inject, signal, ChangeDetectionStrategy} from "@angular/core"
import {toObservable, toSignal} from "@angular/core/rxjs-interop"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterModule} from "@angular/router"
import {TemplateReference} from "@cm/template-nodes"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {GetTemplateDetailsForTemplateReferenceInspectorGQL} from "@template-editor/components/inspectors/template-reference-inspector/template-reference-inspector.generated"
import {catchError, from, of, switchMap} from "rxjs"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {SelectionPossibilities, ValueSlotComponent} from "@app/template-editor/components/value-slot/value-slot.component"
import {BaseInspectorComponent} from "@app/template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@app/template-editor/components/inspectors/inspector-section/inspector-section.component"
import {TemplateThumbnailComponent} from "@template-editor/components/template-thumbnail/template-thumbnail.component"
import {z} from "zod/v4-mini"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-reference-inspector",
    templateUrl: "./template-reference-inspector.component.html",
    styleUrl: "./template-reference-inspector.component.scss",
    imports: [InspectorSectionComponent, ValueSlotComponent, ButtonComponent, RouterModule, MatTooltipModule, TemplateThumbnailComponent],
})
export class TemplateReferenceInspectorComponent extends BaseInspectorComponent<TemplateReference> {
    private readonly templateGql = inject(GetTemplateDetailsForTemplateReferenceInspectorGQL)

    readonly $templateId = computed(() => {
        return this.$parameters().templateId
    })
    readonly $templateRevisionId = computed(() => {
        return this.$parameters().templateRevisionId
    })
    private readonly $templateData = toSignal(
        toObservable(this.$templateId).pipe(
            switchMap((templateId) => {
                return from(fetchThrowingErrors(this.templateGql)({legacyId: templateId}))
            }),
            catchError(() => of(null)),
        ),
        {
            initialValue: null,
        },
    )

    readonly $boolean = signal(z.boolean())
    readonly $number = signal(z.number())
    readonly $autoUpdate = computed(() => {
        return this.$parameters().templateRevisionId === null
    })
    readonly $latestTemplateRevisionId = computed(() => {
        const templateData = this.$templateData()
        return templateData?.template.latestRevision?.legacyId
    })

    readonly $revisionPossibilities = computed<SelectionPossibilities<number>>(() => {
        const templateData = this.$templateData()
        return (
            templateData?.template.revisions?.map((revision) => ({
                value: revision.legacyId,
                name: `v${revision.number}`,
            })) ?? []
        )
    })

    onChangeAutoUpdate(autoUpdate: unknown) {
        if (typeof autoUpdate !== "boolean") return
        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({templateRevisionId: autoUpdate ? null : this.$latestTemplateRevisionId()})
        })
    }

    updateToLatestTemplateRevision() {
        const templateReference = this.$node()
        if (!templateReference) return
        const latestTemplateRevisionId = this.$latestTemplateRevisionId()
        if (latestTemplateRevisionId === undefined) return
        this.sceneManagerService.modifyTemplateGraph(() => {
            templateReference.updateParameters({templateRevisionId: latestTemplateRevisionId})
        })
    }
}
